import { useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SUBJECT_ROUTES,
  WAR_ROOM_PRIORITY_MAP,
  type SignalSubject,
  type WarRoomPriority,
  type WarRoomRuleKey,
  type WarRoomSignal,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Badge, EmptyState, ErrorState, Modal, PageHead, Stat } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { SessionBar, SessionModal } from './WarRoomSession';

interface RuleGroup {
  rule: WarRoomRuleKey;
  label: string;
  description: string;
  priority: WarRoomPriority;
  threshold: number;
  thresholdUnit: 'DAYS' | 'MONEY';
  action: 'CALL' | 'TASK' | 'MAIL';
  total: number;
  amount: number;
  signals: WarRoomSignal[];
}

interface Board {
  groups: RuleGroup[];
  summary: { total: number; critical: number; amount: number; dismissed: number };
  generatedAt: string;
}

/** كم تذكرةً تظهر في العمود قبل أن يبدأ تمريرُه الداخليّ. */
const VISIBLE_TICKETS = 5;

const ACTION_LABELS: Record<RuleGroup['action'], string> = {
  CALL: 'حملة اتصال',
  TASK: 'توزيع مهام',
  MAIL: 'حملة بريد',
};

/**
 * ترويسةُ العمود تثبت أعلى الشاشة حين تمرّ الصفحةُ فوقها، فيُعرف
 * العمودُ وإن طال.
 *
 * `position: sticky` لا يعمل هنا: اللوحةُ حاويةُ تمريرٍ أفقيّ، واللاصقُ
 * يلتصق بأقرب حاويةِ تمرير لا بالصفحة. فتُزاح الترويسةُ يدويًّا بقدر ما
 * جاوزت الصفحةُ رأسَ اللوحة، وتُقصر على طول عمودها.
 *
 * وعجلةُ الفأرة فوق عمودٍ تُمرّر الصفحةَ أوّلًا حتى تظهر اللوحةُ كلُّها
 * (خمسُ تذاكر)، ثمّ يبدأ تمريرُ العمود الداخليّ — لا العكس، وإلّا احتُجز
 * المؤشّرُ داخل العمود ولم تنزل الصفحة.
 */
function useBoardPin(board: RefObject<HTMLDivElement>, deps: unknown[]) {
  useLayoutEffect(() => {
    const el = board.current;
    const main = el?.closest<HTMLElement>('.main');
    if (!el || !main) return;

    const pin = () => {
      const offset = Math.max(0, main.getBoundingClientRect().top - el.getBoundingClientRect().top);
      for (const head of el.querySelectorAll<HTMLElement>('.wr-column-head')) {
        const column = head.parentElement;
        const max = column ? Math.max(0, column.offsetHeight - head.offsetHeight) : 0;
        head.style.transform = offset ? `translateY(${Math.min(offset, max)}px)` : '';
      }
    };

    const wheel = (e: WheelEvent) => {
      if (e.deltaY <= 0 || e.ctrlKey) return;
      const step = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const gap = el.getBoundingClientRect().bottom - main.getBoundingClientRect().bottom;
      if (gap > 1) {
        main.scrollTop += Math.min(step, gap);
        e.preventDefault();
        // حدثُ التمرير يأتي في الإطار التالي؛ التثبيتُ الفوريّ يمنع قفزةً.
        pin();
      }
    };

    pin();
    main.addEventListener('scroll', pin, { passive: true });
    window.addEventListener('resize', pin);
    el.addEventListener('wheel', wheel, { passive: false });
    return () => {
      main.removeEventListener('scroll', pin);
      window.removeEventListener('resize', pin);
      el.removeEventListener('wheel', wheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

/**
 * غرفة العمليات.
 *
 * الشاشة الوحيدة التي تُفتح لتُفرَغ. كل ما فيها يستحق تدخّلًا اليوم، وما
 * عُولج يختفي منها من تلقاء نفسه لأن الإشارات تُحسب من حالة البيانات لا
 * تُخزَّن كمهام.
 */
export function WarRoom() {
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();

  const [mine, setMine] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);
  const [campaignFor, setCampaignFor] = useState<RuleGroup | null>(null);
  /*
   * نطاق الجلسة الجارية يُصفّي اللوحة تحتها.
   *
   * جلسةٌ تطلب تصفية «العملاء المهملين» ثم تعرض تحتها ثماني قواعد أخرى
   * تُشتّت من دُعي إليها: ما يُطلب تصفيته هو ما يُعرض. وقائمةٌ لا قاعدة:
   * الجلسةُ قد تجمع أكثر من قاعدة.
   */
  const [sessionRules, setSessionRules] = useState<string[]>([]);
  const [sessionActive, setSessionActive] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const toast = useToast();

  const canManage = atLeast('MANAGER');
  const query = {
    mine: mine || undefined,
    includeDismissed: showDismissed || undefined,
    rules: sessionRules.length ? sessionRules.join(',') : undefined,
    perRule: 25,
  };

  const board = useQuery({
    queryKey: ['war-room', query],
    queryFn: () => api<Board>(`/crm/war-room${qs(query)}`),
    // البيانات تتغيّر بفعل الفريق طوال اليوم، فإعادة القراءة عند العودة مقصودة.
    refetchOnWindowFocus: true,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['war-room'] });

  const summary = board.data?.summary;
  const boardEl = useRef<HTMLDivElement>(null);
  useBoardPin(boardEl, [board.data]);

  return (
    <div className="page">
      <PageHead
        title="غرفة العمليات"
        subtitle="ما يستحق تدخّلًا اليوم، مرتَّبًا بحسب الخطورة"
        actions={
          <>
            {/*
              الأزرارُ كلُّها في صفٍّ واحد أعلى الصفحة.
              المرشِّحاتُ والإجراءاتُ كانت موزَّعةً على ثلاثة صفوف بينها المقاييس،
              فيبحث المستخدمُ عن «ما يخصّني» تحت الأرقام وعن «جلسة تركيز» فوقها.
            */}
            <div className="row" style={{ gap: 2 }}>
              <button
                className={`btn btn-sm${!mine ? ' btn-primary' : ''}`}
                onClick={() => setMine(false)}
              >
                الكل
              </button>
              <button
                className={`btn btn-sm${mine ? ' btn-primary' : ''}`}
                onClick={() => setMine(true)}
              >
                ما يخصّني
              </button>
            </div>

            <label className="row" style={{ cursor: 'pointer', gap: 6 }}>
              <input
                type="checkbox"
                checked={showDismissed}
                onChange={(e) => setShowDismissed(e.target.checked)}
              />
              <span className="small">إظهار المصروف والمؤجَّل</span>
            </label>

            {canManage && !sessionActive && (
              <button className="btn" onClick={() => setScheduling(true)}>
                <Icon name="plus" size={15} />
                جلسة تركيز
              </button>
            )}

            <button className="btn" onClick={() => void board.refetch()}>
              <Icon name="refresh" size={15} />
              تحديث
            </button>
            {/* القواعدُ تُضبط من الإعدادات — هنا رابطٌ إليها لا زرُّ ضبط. */}
            {canManage && (
              <Link
                to="/settings/war-room-rules"
                className="btn btn-ghost"
                title="قواعد الرصد في الإعدادات"
              >
                <Icon name="settings" size={15} />
                قواعد الرصد
              </Link>
            )}
          </>
        }
      />

      <SessionBar onScopeChange={setSessionRules} onActiveChange={setSessionActive} />

      <div className="stats">
        <Stat
          label="إشارات مفتوحة"
          value={summary ? summary.total : '—'}
          note={summary ? `عبر ${plural(board.data!.groups.length, WORDS.rule)}` : undefined}
        />
        <Stat
          label="حرجة"
          value={<span className={summary?.critical ? 'neg' : ''}>{summary?.critical ?? '—'}</span>}
          note="تحتاج تدخّلًا اليوم"
        />
        <Stat
          label="قيمة معرَّضة"
          value={<span className="num">{summary ? money(summary.amount) : '—'}</span>}
          note="مجموع ما ترتبط به الإشارات"
        />
        <Stat
          label="مصروفة ومؤجَّلة"
          value={summary?.dismissed ?? '—'}
          note="راجعها من زر الإظهار"
        />
      </div>

      {board.isError && <ErrorState error={board.error} onRetry={() => void board.refetch()} />}

      {board.isPending && (
        <div className="wr-board">
          <div className="skeleton wr-column" />
          <div className="skeleton wr-column" />
          <div className="skeleton wr-column" />
        </div>
      )}

      {board.data && board.data.groups.length === 0 && (
        <EmptyState
          title="لا شيء يستحق التدخل الآن"
          text="لا عملاء مهمَلين ولا عروض معلَّقة ولا فواتير متأخرة تتجاوز الحدود المضبوطة. عُد غدًا، أو راجع قواعد الرصد في الإعدادات إن كنت تتوقع نتائج."
        />
      )}

      {/*
        لوحةُ أعمدة: كلُّ قاعدةٍ عمودٌ بجوار الأخرى، تذاكرُه مرصوصةٌ تحت
        رأسه وتُمرَّر رأسيًّا، واللوحةُ تُمرَّر أفقيًّا لما زاد عن الشاشة —
        فتُرى القواعدُ كلُّها جنبًا إلى جنب ويُقارَن حجمُ كلٍّ بنظرة.
      */}
      {board.data && board.data.groups.length > 0 && (
        <div className="wr-board" role="list" ref={boardEl}>
          {board.data.groups.map((group) => (
            <SignalGroup
              key={group.rule}
              group={group}
              canManage={canManage}
              onChanged={refresh}
              onCampaign={() => setCampaignFor(group)}
            />
          ))}
        </div>
      )}

      {scheduling && (
        <SessionModal
          onClose={() => setScheduling(false)}
          onCreated={() => {
            setScheduling(false);
            void refresh();
            toast('جُدولت الجلسة');
          }}
        />
      )}

      {campaignFor && (
        <CampaignModal
          group={campaignFor}
          onClose={() => setCampaignFor(null)}
          onDone={() => {
            setCampaignFor(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

function SignalGroup({
  group,
  canManage,
  onChanged,
  onCampaign,
}: {
  group: RuleGroup;
  canManage: boolean;
  onChanged: () => void;
  onCampaign: () => void;
}) {
  const priority = WAR_ROOM_PRIORITY_MAP[group.priority];
  const body = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  /*
   * العمودُ يُظهر خمسَ تذاكرَ ثمّ يُمرَّر داخله.
   *
   * الارتفاعُ يُقاس من التذاكر نفسها لا يُقدَّر: تذكرةٌ فيها هاتفٌ وسببٌ طويل
   * أطولُ من أخرى، ورقمٌ ثابت يُظهر أربعًا هنا وستًّا هناك. والصفحةُ نفسها
   * تُمرَّر عاديًّا حتى تبلغ اللوحة، ثمّ يبدأ تمريرُ العمود.
   */
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const cards = [...el.querySelectorAll<HTMLElement>('.signal-ticket')].slice(0, VISIBLE_TICKETS);
    if (cards.length < VISIBLE_TICKETS) {
      setMaxHeight(undefined);
      return;
    }
    const gap = parseFloat(getComputedStyle(el).rowGap || '0') || 0;
    const pad = parseFloat(getComputedStyle(el).paddingTop || '0') || 0;
    const height = cards.reduce((sum, c) => sum + c.getBoundingClientRect().height, 0);
    setMaxHeight(Math.ceil(height + gap * (VISIBLE_TICKETS - 1) + pad * 2));
  }, [group.signals]);

  return (
    <section
      className={`wr-column signal-card is-${group.priority.toLowerCase()}`}
      role="listitem"
      aria-label={group.label}
    >
      <header className="wr-column-head">
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          <h2 className="wr-column-title">{group.label}</h2>
          <Badge kind={priority.kind}>{priority.label}</Badge>
        </div>
        <div className="row tiny mute" style={{ gap: 8 }}>
          <span>{plural(group.total, WORDS.signal)}</span>
          {group.amount > 0 && <span className="num">{money(group.amount)} معرَّضة</span>}
        </div>
        <p className="tiny mute wr-column-desc" title={group.description}>
          {group.description}
        </p>
        {canManage && (
          <button className="btn btn-sm btn-primary" onClick={onCampaign}>
            <Icon name="zap" size={14} />
            {ACTION_LABELS[group.action]}
          </button>
        )}
      </header>

      {/* تذاكرُ العمود تُمرَّر رأسيًّا وحدَها؛ الرأسُ يبقى ثابتًا فوقها. */}
      <div className="wr-column-body" ref={body} style={{ maxHeight }}>
        {group.signals.map((signal) => (
          <SignalTicket key={signal.key} signal={signal} onChanged={onChanged} />
        ))}

        {group.total > group.signals.length && (
          <p className="tiny mute" style={{ padding: '4px 2px' }}>
            يُعرض {group.signals.length} من {group.total}. عالج المعروض لتظهر البقية.
          </p>
        )}
      </div>
    </section>
  );
}

function SignalTicket({ signal, onChanged }: { signal: WarRoomSignal; onChanged: () => void }) {
  const [menu, setMenu] = useState(false);

  const dismiss = useMutation({
    mutationFn: (snoozeDays: number) =>
      api('/crm/war-room/dismiss', {
        method: 'POST',
        body: { signalKey: signal.key, snoozeDays },
      }),
    onSuccess: () => {
      setMenu(false);
      onChanged();
    },
  });

  const href = `${SUBJECT_ROUTES[signal.subject as SignalSubject]}/${signal.subjectId}`;

  return (
    <article className="signal-ticket">
      <div className="signal-ticket-head">
        <Link to={href} className="cell-strong link signal-ticket-title">
          {signal.title}
        </Link>
        {signal.amount ? (
          <span className="signal-ticket-amount num">{money(signal.amount)}</span>
        ) : null}
      </div>

      {signal.phone && (
        <a className="tiny mute num" href={`tel:${signal.phone}`} dir="ltr">
          {signal.phone}
        </a>
      )}

      <p className="signal-ticket-reason">{signal.reason}</p>

      {/* على مَن تقعُ الإشارةُ، وهل رُفعت لطولِ تأخّرها. */}
      <div className="signal-ticket-meta">
        <span className="signal-owner">
          <Icon name="user" size={12} />
          {signal.ownerName ?? 'بلا مسؤول'}
        </span>
        {signal.escalated && (
          <span className="signal-escalated" title="رُفعت أولويّتها لطول تجاوزها الحدّ">
            <Icon name="arrowUp" size={12} /> مُصعَّدة
          </span>
        )}
      </div>

      <div className="signal-ticket-actions">
        {menu ? (
          <>
            <button
              className="btn btn-sm"
              onClick={() => dismiss.mutate(1)}
              disabled={dismiss.isPending}
            >
              غدًا
            </button>
            <button
              className="btn btn-sm"
              onClick={() => dismiss.mutate(7)}
              disabled={dismiss.isPending}
            >
              أسبوع
            </button>
            <button
              className="btn btn-sm"
              onClick={() => dismiss.mutate(0)}
              disabled={dismiss.isPending}
            >
              صرف
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => setMenu(false)}>
              إلغاء
            </button>
          </>
        ) : (
          <button className="btn btn-sm btn-ghost" onClick={() => setMenu(true)}>
            تأجيل أو صرف
          </button>
        )}
      </div>
    </article>
  );
}

/* ========================================================================== */

function CampaignModal({
  group,
  onClose,
  onDone,
}: {
  group: RuleGroup;
  onClose: () => void;
  onDone: () => void;
}) {
  const [assigneeId, setAssigneeId] = useState('');
  const [dueInDays, setDueInDays] = useState('0');
  const [subject, setSubject] = useState('');
  const [note, setNote] = useState('');
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(group.signals.map((s) => s.key)),
  );

  const members = useQuery({
    queryKey: ['members', 'options'],
    queryFn: () => api<{ items: Array<{ userId: string; name: string }> }>('/auth/members'),
  });

  const run = useMutation({
    mutationFn: () =>
      api<{ created: number; skipped: number; assignees: number }>('/crm/war-room/campaign', {
        method: 'POST',
        body: {
          rule: group.rule,
          signalKeys: [...selected],
          assigneeId: assigneeId || null,
          dueInDays: Number(dueInDays) || 0,
          subject: subject.trim() || undefined,
          note: note.trim() || undefined,
        },
      }),
  });

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  if (run.isSuccess) {
    const { created, skipped, assignees } = run.data;
    return (
      <Modal
        title="تمت الحملة"
        onClose={onDone}
        footer={
          <button className="btn btn-primary" onClick={onDone}>
            تم
          </button>
        }
      >
        <p className="mb-4">
          أُنشئت {plural(created, WORDS.task)} موزَّعة على {plural(assignees, WORDS.member)}.
        </p>
        {skipped > 0 && (
          <div className="alert alert-info">
            تُخطّيت {plural(skipped, WORDS.signal)} لوجود مهمة مفتوحة بالموضوع نفسه، فلا تتكرر
            المهمة في قائمة المندوب.
          </div>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title={ACTION_LABELS[group.action]}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose} type="button">
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => run.mutate()}
            disabled={selected.size === 0 || run.isPending}
          >
            {run.isPending ? 'جارٍ الإنشاء' : `إنشاء ${plural(selected.size, WORDS.task)}`}
          </button>
        </>
      }
    >
      {run.isError && (
        <div className="alert alert-danger mb-4">
          {run.error instanceof Error ? run.error.message : 'تعذّر إنشاء الحملة.'}
        </div>
      )}

      <p className="small mute mb-4">
        تتحوّل كل إشارة مختارة إلى مهمة مرتبطة بسجلها، فتظهر في ملف العميل وفي قائمة المكلَّف معًا.
      </p>

      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="c-assignee">
            المكلَّف
          </label>
          <select
            id="c-assignee"
            className="select"
            value={assigneeId}
            onChange={(e) => setAssigneeId(e.target.value)}
          >
            <option value="">صاحب كل سجل</option>
            {members.data?.items.map((m) => (
              <option key={m.userId} value={m.userId}>
                {m.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            الافتراضي يوزّع كل مهمة على من يملك سجلها، فلا تتراكم على شخص واحد.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="c-due">
            التنفيذ خلال
          </label>
          <select
            id="c-due"
            className="select"
            value={dueInDays}
            onChange={(e) => setDueInDays(e.target.value)}
          >
            <option value="0">اليوم</option>
            <option value="1">غدًا</option>
            <option value="3">ثلاثة أيام</option>
            <option value="7">أسبوع</option>
          </select>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="c-subject">
            عنوان المهمة
          </label>
          <input
            id="c-subject"
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={group.label}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="c-note">
            تعليمات للفريق
          </label>
          <textarea
            id="c-note"
            className="textarea"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <span className="tiny mute">تُضاف إلى نص كل مهمة مع بيانات السجل وسبب الإشارة.</span>
        </div>
      </div>

      <h4 className="section-title">
        السجلات ({selected.size} من {group.signals.length})
      </h4>

      <div className="col scroll-box" style={{ gap: 4 }}>
        {group.signals.map((s) => (
          <label key={s.key} className="row pick-row" style={{ cursor: 'pointer', gap: 8 }}>
            <input type="checkbox" checked={selected.has(s.key)} onChange={() => toggle(s.key)} />
            <span className="small" style={{ flex: 1 }}>
              {s.title}
            </span>
            <span className="tiny mute">{s.reason}</span>
          </label>
        ))}
      </div>
    </Modal>
  );
}
